import { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sparkles, Loader2, GitMerge, Check } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, STATUS_LABELS } from '../lib/utils';

export default function CrmCleanupCard() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState('');
  const [masters, setMasters] = useState({});
  const [renames, setRenames] = useState({});

  const runScan = async () => {
    setBusy('scan');
    try {
      const r = await api.get('/admin/cleanup/scan');
      setScan(r.data);
      const m = {};
      r.data.duplicate_groups.forEach((g, i) => { m[i] = g.records[0].id; });
      setMasters(m);
      toast.success(`${r.data.duplicate_groups.length} duplicate groups · ${r.data.normalization_suggestions.length} naming suggestions`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); } finally { setBusy(''); }
  };

  const mergeGroup = async (gi) => {
    const g = scan.duplicate_groups[gi];
    const master = masters[gi];
    const dups = g.records.filter((r) => r.id !== master).map((r) => r.id);
    const m = g.records.find((r) => r.id === master);
    if (!window.confirm(`Merge ${dups.length} duplicate contact record(s) into "${m?.primary_contact} — ${m?.company_name}"? All notes, tasks, meetings, documents and history move to the master. Duplicates are archived.`)) return;
    setBusy(`merge-${gi}`);
    try {
      await api.post('/admin/cleanup/merge-group', { master_id: master, duplicate_ids: dups });
      toast.success('Group merged');
      setScan({ ...scan, duplicate_groups: scan.duplicate_groups.filter((_, i) => i !== gi) });
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); } finally { setBusy(''); }
  };

  const applyRename = async (s) => {
    setBusy(`rn-${s.id}`);
    try {
      await api.post('/admin/cleanup/rename', {
        id: s.id,
        company_name: renames[`${s.id}-c`] ?? s.suggested_company,
        primary_contact: renames[`${s.id}-p`] ?? s.suggested_contact,
      });
      toast.success('Updated');
      setScan({ ...scan, normalization_suggestions: scan.normalization_suggestions.filter((x) => x.id !== s.id) });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };

  return (
    <Card data-testid="crm-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-magenta" /> CRM Cleanup (Duplicate Contacts & Naming)</CardTitle>
        <p className="text-xs text-slate-500 mt-1">Scans for duplicate CONTACT records — same email, same phone, or same person at the same company. Multiple people at the same company are never flagged. Merges keep the OLDEST record as master, move all notes, tasks, meetings, documents and history, and archive the duplicate shells. Everything is audit-logged.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button variant="outline" onClick={runScan} disabled={!!busy} data-testid="cleanup-scan">
          {busy === 'scan' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Scan CRM for Duplicates
        </Button>

        {scan && (
          <>
            <div className="text-xs text-slate-500">{scan.total_active_records} active records · {scan.duplicate_groups.length} duplicate groups · {scan.normalization_suggestions.length} naming suggestions</div>
            {scan.duplicate_groups.map((g, gi) => (
              <div key={gi} className="border border-amber-200 bg-amber-50/40 rounded-xl p-3" data-testid={`dup-group-${gi}`}>
                {g.reasons?.length > 0 && (
                  <div className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5" data-testid={`dup-group-${gi}-reasons`}>{g.reasons.join(' · ')}</div>
                )}
                <div className="space-y-1.5">
                  {g.records.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer flex-wrap">
                      <input type="radio" name={`master-${gi}`} checked={masters[gi] === r.id} onChange={() => setMasters({ ...masters, [gi]: r.id })} className="accent-[#C2185B]" />
                      <span className="font-medium text-navy">{r.primary_contact}</span>
                      <span className="text-xs text-slate-600">{r.company_name}</span>
                      <span className="text-xs text-slate-500">{[r.email, r.phone].filter(Boolean).join(' · ')}{(r.email || r.phone) ? ' · ' : ''}{STATUS_LABELS[r.status] || r.status} · created {fmtDate(r.created_at)}</span>
                      {masters[gi] === r.id && <span className="text-[10px] font-bold text-emerald-600 uppercase">Master</span>}
                    </label>
                  ))}
                </div>
                <Button size="sm" className="mt-2 bg-magenta hover:bg-magenta/90" onClick={() => mergeGroup(gi)} disabled={!!busy} data-testid={`merge-group-${gi}`}>
                  {busy === `merge-${gi}` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5 mr-1.5" />}
                  Merge {g.records.length - 1} into master
                </Button>
              </div>
            ))}
            {scan.normalization_suggestions.map((s) => (
              <div key={s.id} className="border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid={`normalize-${s.id}`}>
                <div className="text-xs text-slate-500 w-full">Current: <strong className="text-navy">{s.company_name}</strong> — {s.primary_contact}</div>
                {s.suggested_company && <Input className="h-8 text-sm w-56" defaultValue={s.suggested_company} onChange={(e) => setRenames({ ...renames, [`${s.id}-c`]: e.target.value })} />}
                {s.suggested_contact && <Input className="h-8 text-sm w-48" defaultValue={s.suggested_contact} onChange={(e) => setRenames({ ...renames, [`${s.id}-p`]: e.target.value })} />}
                <Button size="sm" variant="outline" onClick={() => applyRename(s)} disabled={!!busy} data-testid={`apply-rename-${s.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Apply
                </Button>
              </div>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
